import { useQuery } from '@tanstack/react-query'
import { trackError } from '@/lib/analytics'
import { useChannel } from '@/context/ChannelContext'
import { usePollsAccess } from '@/config/polls'
import { fetchPollsList, type PollStatus } from '@/api/polls'

/**
 * React-query wrapper над fetchPollsList — с двойной защитой из §7.5:
 * (1) `enabled: allowed && !!channelUUID` — гейт внутри hook body, не только
 *     в UI-guard. Иначе fetch запустится в общем DesktopShell/ChannelProvider
 *     контексте и повалит всех LK-юзеров 404-м → Sentry.ErrorBoundary
 *     (audit critical #6 «gate leak via ChannelProvider fetch»).
 * (2) `queryFn` возвращает `null` вместо throw при ошибке (`fetchPollsList`
 *     сам ловит), плюс `onError → trackError` для наблюдаемости 403 от
 *     MaxWebAppGuard (если бэк ошибочно смонтирует под guard).
 */
export function usePollsList(status: PollStatus | 'all' = 'all') {
  const allowed = usePollsAccess()
  const { selectedChannel } = useChannel()
  const channelUUID = selectedChannel?.uuid

  return useQuery({
    queryKey: ['polls-list', channelUUID, status],
    queryFn: () => fetchPollsList({ channelUUID: channelUUID!, status }),
    enabled: allowed && !!channelUUID,
    staleTime: 30_000,
    meta: {
      onError: (e: unknown) => trackError(e, 'polls-list'),
    },
  })
}
